import React, { useState } from 'react';
import { Card, Dropdown } from 'react-bootstrap';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { IconBuildingBank } from '@tabler/icons-react';
import useIndexBank from '../../../services/properties/hooks/useIndexBank';
import { PropertyBank } from '../../../services/properties/response/IndexBankResponse';
import { useDeleteBank } from '../../../services/properties/hooks/useDeleteBank';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import FormError from '../../../shared/components/errors/FormError';
// Reuse dialog form dari property-management — 100% generic (cuma terima propertyId/bankId),
// tidak ada logic yang terikat konteks dev-only, jadi aman dipakai lintas modul di sini.
import CreateBankAccountDialog from '../../property-management/components/CreateBankAccountDialog';
import UpdateBankAccountDialog from '../../property-management/components/UpdateBankAccountDialog';

interface BankAccountCardProps {
    propertyId: string;
    canManage: boolean;
}

const BankAccountCard: React.FC<BankAccountCardProps> = ({ propertyId, canManage }) => {
    const { data: bankAccounts, isSuccess, isError, isFetching } = useIndexBank({
        filters: { property_id: propertyId },
    });

    const { mutate: deleteBank, isPending: isDeleting } = useDeleteBank();
    const queryClient = useQueryClient();

    const [showCreate, setShowCreate] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);
    const [selectedBankId, setSelectedBankId] = useState<string | null>(null);

    const handleOpenUpdate = (bankId: string) => {
        setSelectedBankId(bankId);
        setShowUpdate(true);
    };

    const handleCloseUpdate = () => {
        setSelectedBankId(null);
        setShowUpdate(false);
    };

    const handleRemove = (bankAccount: PropertyBank) => {
        toast.loading('Removing bank account...', { id: 'remove-my-property-bank' });

        deleteBank({ id: bankAccount.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['banks'].includes(key as string))
                });
                toast.success('Bank account removed successfully', { id: 'remove-my-property-bank' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove bank account', { id: 'remove-my-property-bank' });
            }
        });
    };

    return (
        <>
            <Card className="shadow-sm h-100">
                <Card.Header className="bg-primary text-white d-flex align-items-center justify-content-between">
                    <span>Bank Account</span>
                    {canManage && (
                        <button className="btn btn-sm btn-light" onClick={() => setShowCreate(true)}>
                            Add
                        </button>
                    )}
                </Card.Header>
                <Card.Body>
                    {isError && <FormError />}
                    {isFetching && (
                        <div className="d-flex align-items-center justify-content-center py-3">
                            <FormSpinner />
                        </div>
                    )}
                    {isSuccess && !isFetching && (
                        bankAccounts.data.length > 0 ? (
                            <div className="d-flex flex-column gap-3">
                                {bankAccounts.data.map((bankAccount: PropertyBank) => (
                                    <div key={bankAccount.id} className="d-flex align-items-start justify-content-between border-bottom pb-2">
                                        <div className="d-flex align-items-start">
                                            <IconBuildingBank size={18} className="me-2 flex-shrink-0 mt-1 text-muted" />
                                            <div>
                                                <div className="fw-bold">{bankAccount.bank_name}</div>
                                                <div className="text-muted small">{bankAccount.account_name}</div>
                                                <div className="text-muted small">{bankAccount.account_number}</div>
                                            </div>
                                        </div>
                                        {canManage && (
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" size="sm" id={`bank-actions-${bankAccount.id}`}>
                                                    Actions
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <Dropdown.Item href="#" onClick={() => handleOpenUpdate(bankAccount.id)} disabled={isDeleting}>
                                                        Edit
                                                    </Dropdown.Item>
                                                    <Dropdown.Item href="#" onClick={() => handleRemove(bankAccount)} className="text-danger" disabled={isDeleting}>
                                                        Remove
                                                    </Dropdown.Item>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-muted text-center py-3">No bank accounts found</div>
                        )
                    )}
                </Card.Body>
            </Card>

            {canManage && (
                <>
                    <CreateBankAccountDialog show={showCreate} onClose={() => setShowCreate(false)} propertyId={propertyId} />
                    {selectedBankId && (
                        <UpdateBankAccountDialog show={showUpdate} onClose={handleCloseUpdate} bankId={selectedBankId} />
                    )}
                </>
            )}
        </>
    );
};

export default BankAccountCard;
